import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useController, useForm } from "react-hook-form";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  getPathDatabaseByRole,
  renewToken,
  startLogout,
} from "../../actions/auth";
import {
  ADVERTISER_ROLE,
  PLAN_TYPE_CREDITS,
  ROLES_CHANGE_PASSWORD,
  ROLES_PLATFORMS,
  SUPER_ADMIN_ROLE,
} from "../../utils/constants";
import Popover from "@mui/material/Popover";
import ButtonMUI from "@mui/material/Button";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Loader from "../Loaders/Loader";
import {
  setShowBackdrop,
  setModalOpenState,
  setShowModalSelectSubscription,
} from "../../actions/ui";
import { startDeleteAdvertiserFromAgency } from "../../actions/owner";
import {
  startChangeAdvertiserIDCustomClaim,
  startGetAdvertisersByAgency,
  startGetBusinessNameByAdvertiserIDs,
  startGetLogoUrlByAdvertiserIDs,
  startGetSuperAdminAgencyGroups,
} from "../../actions/user";
import _ from "lodash";
import { useTranslationApp } from "../../lib/i18next";
import { ConfirmAlert, SimpleAlert } from "../Alerts/Alerts";
import {
  ADVERTISER_SELECTOR_BRANDS_COUNT,
  ADVERTISER_SELECTOR_BRANDS_COUNT_SINGLE,
  ADVERTISER_SELECTOR_NO_RESULTS,
  ADVERTISER_SELECTOR_SEARCH_PLACEHOLDER,
  ADVERTISER_SELECTOR_TRY_ANOTHER_NAME,
  BUTTON_CANCEL,
  BUTTON_DELETE_CONTENT,
  CREATE_BRAND_BUTTON,
  LOGOUT,
  ADVERTISER_SELECTOR_CONFIGURE_LIMITS_BUTTON,
  DELETE_BRAND_CONFIRMATION_DESCRIPTION,
  DELETE_BRAND_CONFIRMATION_TITLE,
  DELETE_BRAND_ERROR,
  DELETE_BRAND_MAIN_CANNOT_DELETE,
  DELETE_BRAND_SUCCESS,
  SELECT_BRAND_PLACEHOLDER,
  PASSWORD_TITLE,
} from "../../i18n/keysTranslations";
import ModalAddAdvertiser from "../../pages/AdminDashboard/components/ModalAddAdvertiser";
import ModalChangePassword from "../Modal/ModalChangePassword";
import Button from "../Buttons/Button";
import { store } from "../../app/store";

const sortAdvertisersByName = (first, second) => {
  const firstName = first.name.toLowerCase();
  const secondName = second.name.toLowerCase();
  return firstName.localeCompare(secondName);
};

const fetchAdvertiserMetadata = async (dispatch, advertiserIDs) => {
  const [advertisersBusiness, advertisersLogo] = await Promise.all([
    dispatch(startGetBusinessNameByAdvertiserIDs({ advertiserIDs })),
    dispatch(startGetLogoUrlByAdvertiserIDs({ advertiserIDs })),
  ]);

  return { advertisersBusiness, advertisersLogo };
};

const mapToAdvertiserOptions = ({
  advertiserIDs,
  advertisersBusiness,
  advertisersLogo,
  agencyId,
  agencyName,
}) =>
  advertiserIDs.map((advertiserID) => ({
    name: advertisersBusiness[advertiserID] || advertiserID,
    id: advertiserID,
    logoUrl: advertisersLogo[advertiserID] || null,
    ...(agencyId != null ? { agencyId } : {}),
    ...(agencyName != null ? { agencyName } : {}),
  }));

const BRAND_PALETTE = [
  { bg: "#f59e0b", fg: "#ffffff" },
  { bg: "#10b981", fg: "#ffffff" },
  { bg: "#0ea5e9", fg: "#ffffff" },
  { bg: "#f43f5e", fg: "#ffffff" },
  { bg: "#8b5cf6", fg: "#ffffff" },
  { bg: "#f97316", fg: "#ffffff" },
  { bg: "#14b8a6", fg: "#ffffff" },
  { bg: "#ec4899", fg: "#ffffff" },
];

const ADVERTISER_SELECTOR_POPOVER_WIDTH = 288;

const ADVERTISER_SELECTOR_NEUTRAL = {
  50: "#fafafa",
  100: "#f5f5f5",
  200: "#e5e5e5",
  300: "#d4d4d4",
  800: "#262626",
  900: "#171717",
};

const ADVERTISER_SELECTOR_POPOVER_PAPER_SX = {
  mt: 1,
  width: ADVERTISER_SELECTOR_POPOVER_WIDTH,
  p: 0,
  overflow: "hidden",
  borderRadius: "16px",
  bgcolor: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(24px)",
  border: `1px solid rgba(229, 229, 229, 0.8)`,
  boxShadow:
    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
};

const ADVERTISER_SELECTOR_TRIGGER_BUTTON_SX = {
  height: 44,
  px: "8px",
  pr: "12px",
  py: 0,
  gap: "10px",
  borderRadius: "9999px",
  textTransform: "none",
  justifyContent: "space-between",
  alignItems: "center",
  minWidth: 0,
  width: "100%",
  maxWidth: "100%",
  bgcolor: ADVERTISER_SELECTOR_NEUTRAL[100],
  border: "none",
  boxShadow: "none",
  transition:
    "background-color 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    bgcolor: "rgba(229, 229, 229, 0.8)",
    boxShadow: "none",
  },
};

const getBrandColorPaletteIndexForId = (id) => {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash << 5) - hash + id.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % BRAND_PALETTE.length;
};

const getInitials = ({ name }) => {
  if (!name?.trim()) {
    return "";
  }
  return name
    .split(/\s+/u)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const AdvertiserSelectorBrandAvatar = ({
  option,
  size,
  isSelected = false,
  isTrigger = false,
}) => {
  const palette = BRAND_PALETTE[getBrandColorPaletteIndexForId(option.id)];

  if (option.logoUrl) {
    return (
      <Avatar
        src={option.logoUrl}
        alt={option.name}
        className={isTrigger ? undefined : "advertiser-trigger-avatar"}
        sx={{
          width: size,
          height: size,
          borderRadius: isTrigger ? "50%" : "12px",
          bgcolor: "common.white",
          transition: "transform 200ms cubic-bezier(0.4, 0, 0.2, 1)",
          ...(isSelected && !isTrigger ? { bgcolor: "common.white" } : {}),
          "& .MuiAvatar-img": {
            objectFit: "contain",
            objectPosition: "center",
          },
        }}
      />
    );
  }

  return (
    <Avatar
      className={isTrigger ? undefined : "advertiser-trigger-avatar"}
      sx={{
        width: size,
        height: size,
        borderRadius: isTrigger ? "50%" : "12px",
        transition: "transform 200ms cubic-bezier(0.4, 0, 0.2, 1)",
        ...(isSelected
          ? !isTrigger
            ? {
                bgcolor: "common.white",
                color: "grey.900",
              }
            : {}
          : {
              bgcolor: palette.bg,
              color: palette.fg,
            }),
        fontSize: size >= 34 ? "0.75rem" : "0.6875rem",
        fontWeight: 700,
      }}
    >
      {getInitials({ name: option.name })}
    </Avatar>
  );
};

const AdvertiserSelectorTriggerButton = ({
  disabled,
  emptySelection,
  triggerPlaceholder,
  selectedOption,
  isPopoverOpen,
  onToggle,
}) => (
  <ButtonMUI
    variant="text"
    disableElevation
    disableRipple={false}
    disabled={disabled}
    onClick={(e) => {
      onToggle({ event: e });
    }}
    aria-expanded={isPopoverOpen}
    aria-haspopup="dialog"
    role="combobox"
    sx={{ ...ADVERTISER_SELECTOR_TRIGGER_BUTTON_SX }}
  >
    {emptySelection ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "10px",
          flex: 1,
          minWidth: 0,
          textAlign: "left",
        }}
      >
        <Avatar
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            bgcolor: ADVERTISER_SELECTOR_NEUTRAL[300],
          }}
        >
          <ApartmentRoundedIcon sx={{ fontSize: 17, color: "grey.600" }} />
        </Avatar>
        <Typography
          sx={{
            flex: 1,
            fontWeight: 500,
            fontSize: "0.875rem",
            color: "#737373",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {triggerPlaceholder}
        </Typography>
      </Box>
    ) : (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "10px",
          flex: 1,
          minWidth: 0,
          textAlign: "left",
        }}
      >
        <Box sx={{ flexShrink: 0, display: "flex" }}>
          <AdvertiserSelectorBrandAvatar
            option={selectedOption}
            size={28}
            isTrigger
          />
        </Box>
        <Typography
          sx={{
            flex: 1,
            fontWeight: 500,
            fontSize: "0.875rem",
            color: "grey.900",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {selectedOption.name}
        </Typography>
      </Box>
    )}
    <ExpandMoreRoundedIcon
      sx={{
        fontSize: "1.125rem",
        color: "#737373",
        flexShrink: 0,
        ml: 0.5,
        alignSelf: "center",
        transform: isPopoverOpen ? "rotate(180deg)" : "none",
        transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    />
  </ButtonMUI>
);

const AdvertiserSelectorPopoverSearch = ({
  searchQuery,
  searchPlaceholderLabel,
  onSearchChange,
}) => (
  <Box
    sx={{
      p: 1.25,
      borderBottom: "1px solid",
      borderColor: "grey.100",
    }}
  >
    <TextField
      size="small"
      placeholder={searchPlaceholderLabel}
      value={searchQuery}
      fullWidth
      onChange={(e) => {
        onSearchChange({ nextValue: e.target.value });
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          bgcolor: "#fafafa",
          borderRadius: "8px",
          fontSize: "0.875rem",
        },
        "& fieldset": { border: "none" },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
          {
            borderWidth: "1px",
            borderColor: "#d4d4d4",
          },
        "& .MuiOutlinedInput-root.Mui-focused": {
          bgcolor: "common.white",
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 18, color: "#a3a3a3" }} />
          </InputAdornment>
        ),
      }}
      inputProps={{
        "aria-label": searchPlaceholderLabel,
      }}
    />
  </Box>
);

const AdvertiserSelectorPopoverBrandsCountRow = ({ brandsCountLabel }) => (
  <Box
    sx={{
      px: 2,
      py: "10px",
      bgcolor: "rgba(250,250,250,0.55)",
      borderBottom: "1px solid",
      borderColor: "grey.100",
    }}
  >
    <Typography
      sx={{
        fontSize: "0.75rem",
        color: "#737373",
        fontWeight: 600,
      }}
    >
      {brandsCountLabel}
    </Typography>
  </Box>
);

const AdvertiserSelectorPopoverNoResults = ({
  emptyTitleLabel,
  emptyHintLabel,
}) => (
  <Box
    sx={{
      py: 4,
      textAlign: "center",
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        bgcolor: "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mx: "auto",
        mb: 1,
      }}
    >
      <SearchIcon sx={{ fontSize: 18, color: "#a3a3a3" }} />
    </Box>
    <Typography
      sx={{
        fontSize: "0.875rem",
        color: "#737373",
      }}
    >
      {emptyTitleLabel}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.75rem",
        color: "#a3a3a3",
        mt: 0.25,
      }}
    >
      {emptyHintLabel}
    </Typography>
  </Box>
);

const AdvertiserSelectorPopoverOptionRow = ({
  advertiser,
  field,
  isSelected,
  showDeleteBrandButton,
  agencyID,
  translate,
  onSelectAdvertiser,
  onConfirmDeleteBrand,
}) => {
  const isMainAdvertiserBrand = advertiser?.id === agencyID;

  return (
    <Box
      className={
        isSelected
          ? "advertiser-selector-row advertiser-selector-row--selected"
          : "advertiser-selector-row"
      }
      onClick={() => {
        void onSelectAdvertiser({ advertiser, field });
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: "12px",
        px: "10px",
        py: 1,
        cursor: "pointer",
        bgcolor: "transparent",
        ...(isSelected
          ? {
              bgcolor: ADVERTISER_SELECTOR_NEUTRAL[900],
            }
          : {}),
        transition: "background-color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          ...(isSelected
            ? {
                bgcolor: ADVERTISER_SELECTOR_NEUTRAL[900],
              }
            : {
                bgcolor: ADVERTISER_SELECTOR_NEUTRAL[100],
              }),
        },
        "&:hover .advertiser-trigger-avatar": {
          ...(isSelected
            ? {}
            : {
                transform: "scale(1.05)",
              }),
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minWidth: 0,
          flex: 1,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <AdvertiserSelectorBrandAvatar
            option={advertiser}
            size={36}
            isSelected={isSelected}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: isSelected ? "common.white" : "#171717",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {advertiser.name}
          </Typography>
          {advertiser.agencyName ? (
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: isSelected ? "grey.400" : "text.secondary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
            >
              {advertiser.agencyName}
            </Typography>
          ) : null}
        </Box>
      </Box>
      {showDeleteBrandButton ? (
        isMainAdvertiserBrand ? (
          <Tooltip
            title={translate(DELETE_BRAND_MAIN_CANNOT_DELETE)}
            placement="left"
            arrow
          >
            <span>
              <IconButton
                className="advertiser-delete-btn"
                size="medium"
                disabled
                aria-label={translate(DELETE_BRAND_MAIN_CANNOT_DELETE)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <DeleteOutlineIcon
                  sx={{
                    fontSize: 24,
                    color: "#a3a3a3",
                    display: "block",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <IconButton
            className="advertiser-delete-btn"
            size="medium"
            aria-label={translate(DELETE_BRAND_CONFIRMATION_TITLE)}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              void onConfirmDeleteBrand({ advertiser, mouseEvent: e });
            }}
            sx={{
              p: "8px",
              borderRadius: "8px",
              color: "#a3a3a3",
              ...(isSelected
                ? {
                    "&:hover": {
                      bgcolor: ADVERTISER_SELECTOR_NEUTRAL[800],
                      color: "#f87171",
                    },
                  }
                : {
                    "&:hover": {
                      bgcolor: "#fef2f2",
                      color: "#ef4444",
                    },
                  }),
            }}
          >
            <DeleteOutlineIcon
              sx={{
                fontSize: 24,
                display: "block",
                ...(isSelected ? { color: "#fff !important" } : {}),
              }}
            />
          </IconButton>
        )
      ) : null}
    </Box>
  );
};

const AdvertiserSelectorPopoverCreateFooter = ({
  createBrandLabel,
  configureLimitsLabel,
  changePasswordLabel,
  logoutLabel,
  onCreateBrand,
  onConfigureLimits,
  onChangePassword,
  onLogout,
  shouldShowCreateBrandOption = false,
  shouldShowConfigureLimitsOption = false,
  shouldShowChangePasswordOption = false,
  shouldShowLogoutOption = false,
}) => (
  <Box
    sx={{
      p: 1,
      borderTop: "1px solid",
      borderColor: "grey.100",
    }}
  >
    {shouldShowConfigureLimitsOption ? (
      <Button
        color="secondary"
        fullWidth
        onClick={onConfigureLimits}
        sx={{
          mb:
            shouldShowCreateBrandOption ||
            shouldShowChangePasswordOption ||
            shouldShowLogoutOption
              ? 1
              : 0,
        }}
      >
        {configureLimitsLabel}
      </Button>
    ) : null}

    {shouldShowCreateBrandOption ? (
      <Button
        color="secondary"
        fullWidth
        onClick={() => {
          onCreateBrand();
        }}
        sx={{
          mb: shouldShowChangePasswordOption || shouldShowLogoutOption ? 1 : 0,
        }}
      >
        <AddRoundedIcon sx={{ fontSize: 18 }} />
        {createBrandLabel}
      </Button>
    ) : null}

    {shouldShowChangePasswordOption ? (
      <Button
        color="secondary"
        fullWidth
        onClick={onChangePassword}
        sx={{
          mb: shouldShowLogoutOption ? 1 : 0,
        }}
      >
        {changePasswordLabel}
      </Button>
    ) : null}

    {shouldShowLogoutOption ? (
      <Button color="secondary" fullWidth onClick={onLogout}>
        {logoutLabel}
      </Button>
    ) : null}
  </Box>
);

const AdvertiserSelectorPopoverList = ({
  filteredAdvertisers,
  field,
  getOptionFromId,
  agencyID,
  showDeleteBrandButton,
  translate,
  onSelectAdvertiser,
  onConfirmDeleteBrand,
  emptyTitleLabel,
  emptyHintLabel,
}) =>
  filteredAdvertisers.length === 0 ? (
    <AdvertiserSelectorPopoverNoResults
      emptyTitleLabel={emptyTitleLabel}
      emptyHintLabel={emptyHintLabel}
    />
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      {filteredAdvertisers.map((advertiser) => {
        const selectedAd = getOptionFromId(field.value);
        const isSelected = selectedAd?.id === advertiser.id;

        return (
          <AdvertiserSelectorPopoverOptionRow
            key={advertiser.id}
            advertiser={advertiser}
            field={field}
            isSelected={isSelected}
            showDeleteBrandButton={showDeleteBrandButton}
            agencyID={agencyID}
            translate={translate}
            onSelectAdvertiser={onSelectAdvertiser}
            onConfirmDeleteBrand={onConfirmDeleteBrand}
          />
        );
      })}
    </Box>
  );

export const SelectAdvertiserID = ({
  disabled,
  placeholder,
  sx = {},
  onChangeAction = () => {},
  onLoadComplete,
}) => {
  const { t } = useTranslationApp();
  const translate = useCallback((key, options) => t(key, options), [t]);

  const { control, setValue } = useForm({
    defaultValues: {
      advertiserID: "",
    },
  });
  const [options, setOptions] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [modalAddAdvertiserOpen, setModalAddAdvertiserOpen] = useState(false);
  const [modalChangePasswordOpen, setModalChangePasswordOpen] = useState(false);

  const dispatch = useDispatchApp();
  const { userID, role, agencyID } = dispatch(getPathDatabaseByRole());
  const userRole = useSelectorApp((state) => state.auth.user?.role);
  const isAnonymous = useSelectorApp((state) => state.auth.user?.isAnonymous);
  const businessName = useSelectorApp((state) => state.user.businessName);
  const subscriptionPlanInfo = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo
  );
  const typeOfPlans = useSelectorApp((state) => state.payments?.typeOfPlans);
  const advertisersLimit = subscriptionPlanInfo?.benefits?.advertisers;
  const advertisersAvailable = useSelectorApp(
    (state) => state.auth.user.advertisersAvailable
  );
  const agenciesAvailable = useSelectorApp(
    (state) => state.auth.user?.agenciesAvailable ?? []
  );
  const {
    field: { value: fieldValue, onChange: onFieldChange },
  } = useController({
    name: "advertiserID",
    control,
  });
  const shouldShowCreateBrandOption =
    userRole === ADVERTISER_ROLE && !isAnonymous;
  const childBrandCount = options.filter(
    (option) => option.id !== agencyID
  ).length;
  const shouldShowConfigureLimitsOption =
    userRole === ADVERTISER_ROLE &&
    !isAnonymous &&
    childBrandCount > 0 &&
    typeOfPlans === PLAN_TYPE_CREDITS;
  const shouldShowLogoutOption = !isAnonymous;
  const shouldShowChangePasswordOption =
    ROLES_CHANGE_PASSWORD.includes(userRole) && !isAnonymous;

  const hasReachedAdvertisersLimit =
    advertisersLimit == null ||
    advertisersLimit <= 0 ||
    options.length >= advertisersLimit;

  const triggerPlaceholder = placeholder ?? translate(SELECT_BRAND_PLACEHOLDER);
  const isPopoverOpen = Boolean(anchorEl);

  const getOptionFromId = (id) => {
    if (!id) return null;
    return options.find((opt) => opt.id === id) || null;
  };

  const filteredAdvertisers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => {
      const nameMatch = `${opt.name}`.toLowerCase().includes(q);
      const agencyMatch = opt.agencyName
        ? `${opt.agencyName}`.toLowerCase().includes(q)
        : false;
      return nameMatch || agencyMatch;
    });
  }, [options, searchQuery]);

  const onLogout = () => {
    dispatch(startLogout());
  };

  const onChangeAdvertiserId = useCallback(
    async ({ newAdvertiserID }) => {
      dispatch(setShowBackdrop(true));
      const response = await dispatch(
        startChangeAdvertiserIDCustomClaim({ advertiserID: newAdvertiserID })
      );
      if (response) {
        onFieldChange(newAdvertiserID);
        onChangeAction(newAdvertiserID);
      }
      dispatch(setShowBackdrop(false));
    },
    [dispatch, onChangeAction, onFieldChange]
  );

  const canDeleteBrand = userRole === ADVERTISER_ROLE && !isAnonymous;
  const showDeleteBrandButton = canDeleteBrand && options.length > 1;

  const confirmDeleteBrand = useCallback(
    async (e, option) => {
      e?.stopPropagation();
      e?.preventDefault();

      const advertiserIdToDelete = option?.id;
      if (!advertiserIdToDelete) return;

      const result = await ConfirmAlert({
        title: translate(DELETE_BRAND_CONFIRMATION_TITLE),
        text: translate(DELETE_BRAND_CONFIRMATION_DESCRIPTION, {
          brandName: option.name || advertiserIdToDelete,
        }),
        confirmButtonText: translate(BUTTON_DELETE_CONTENT),
        cancelButtonText: translate(BUTTON_CANCEL),
      });

      if (!result.isConfirmed) return;

      const remainingOptions = options.filter(
        (o) => o.id !== advertiserIdToDelete
      );
      const nextAdvertiserId = remainingOptions[0]?.id;
      const wasActiveBrand =
        userID === advertiserIdToDelete || fieldValue === advertiserIdToDelete;

      dispatch(setShowBackdrop(true));
      try {
        const res = await dispatch(
          startDeleteAdvertiserFromAgency(advertiserIdToDelete)
        );

        if (res?.ok) {
          setOptions(remainingOptions);
          setAnchorEl(null);
          setSearchQuery("");

          if (wasActiveBrand && nextAdvertiserId) {
            await onChangeAdvertiserId({ newAdvertiserID: nextAdvertiserId });
          } else if (wasActiveBrand && !nextAdvertiserId) {
            onFieldChange("");
            setValue("advertiserID", "");
          }

          await SimpleAlert({
            title: translate(DELETE_BRAND_SUCCESS),
            icon: "success",
          });
        } else {
          await SimpleAlert({
            title: translate(DELETE_BRAND_ERROR),
            icon: "error",
          });
        }
      } finally {
        dispatch(setShowBackdrop(false));
      }
    },
    [
      dispatch,
      fieldValue,
      onFieldChange,
      onChangeAdvertiserId,
      options,
      setValue,
      translate,
      userID,
    ]
  );

  const getInitialData = async () => {
    const authUser = store.getState().auth.user;
    const advertisersAvailableForList = authUser?.advertisersAvailable ?? [];
    const agenciesAvailableForList = authUser?.agenciesAvailable ?? [];
    const isSuperAdmin = authUser?.role === SUPER_ADMIN_ROLE;
    const isSuperAdminMultiAgency =
      isSuperAdmin && agenciesAvailableForList.length > 0;

    let groups = [];
    let metadataAdvertiserIDs = [];

    if (isSuperAdminMultiAgency) {
      const responseAgencyGroups = await dispatch(
        startGetSuperAdminAgencyGroups(agenciesAvailableForList)
      );

      if (!responseAgencyGroups.ok) {
        setIsLoading(false);
        return;
      }

      groups = responseAgencyGroups.data;
      metadataAdvertiserIDs = groups.flatMap((group) => group.advertiserIDs);

      if (metadataAdvertiserIDs.length === 0) {
        setIsLoading(false);
        return;
      }
    } else {
      const responseAdvertisers = await dispatch(startGetAdvertisersByAgency());

      if (!responseAdvertisers.ok) {
        setIsLoading(false);
        return;
      }

      const advertisers = responseAdvertisers.data;

      if (_.isEmpty(advertisers)) {
        setIsLoading(false);
        return;
      }

      metadataAdvertiserIDs = advertisers;
      const displayAdvertiserIDs =
        advertisersAvailableForList.length > 0
          ? advertisersAvailableForList
          : advertisers;

      groups = [{ advertiserIDs: displayAdvertiserIDs }];
    }

    const { advertisersBusiness, advertisersLogo } =
      await fetchAdvertiserMetadata(dispatch, metadataAdvertiserIDs);

    const advertiserOptions = groups.flatMap(
      ({ agencyId, agencyName, advertiserIDs }) => {
        const groupOptions = mapToAdvertiserOptions({
          advertiserIDs,
          advertisersBusiness,
          advertisersLogo,
          agencyId,
          agencyName,
        });

        return isSuperAdminMultiAgency
          ? groupOptions.sort(sortAdvertisersByName)
          : groupOptions;
      }
    );

    if (!isSuperAdminMultiAgency) {
      advertiserOptions.sort(sortAdvertisersByName);
    }

    setOptions(advertiserOptions);
    setIsLoading(false);
  };

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line
  }, [businessName, advertisersAvailable, agenciesAvailable]);

  useEffect(() => {
    if (!isLoading && onLoadComplete) {
      const willBeVisible =
        ROLES_PLATFORMS.includes(role) && options?.length >= 1;

      onLoadComplete(willBeVisible, !willBeVisible);
    }
    // eslint-disable-next-line
  }, [isLoading, options, role]);

  useEffect(() => {
    if (userID && options.length > 0) {
      const selectedOption = options.find((opt) => opt.id === userID);
      if (selectedOption) {
        setValue("advertiserID", userID);
      }
    } else if (userID) {
      setValue("advertiserID", userID);
    }

    // eslint-disable-next-line
  }, [userID, options]);

  const onOpenPopover = ({ event }) => {
    setAnchorEl(event.currentTarget);
  };

  const onClosePopover = () => {
    setAnchorEl(null);
    setSearchQuery("");
  };

  const onTogglePopover = ({ event }) => {
    if (isPopoverOpen) {
      onClosePopover();
    } else {
      onOpenPopover({ event });
    }
  };

  const onSelectAdvertiserFromList = async ({ advertiser, field }) => {
    const selectedOptionId = advertiser?.id;
    const currentValueId = fieldValue || null;

    if (!advertiser) return;

    if (currentValueId !== selectedOptionId) {
      field.onChange(selectedOptionId);
      onClosePopover();
      await onChangeAdvertiserId({ newAdvertiserID: selectedOptionId });
      return;
    }
    onClosePopover();
  };

  const onConfirmDeleteFromRow = async ({ advertiser, mouseEvent }) => {
    await confirmDeleteBrand(mouseEvent, advertiser);
  };

  const onSearchQueryChange = ({ nextValue }) => {
    setSearchQuery(nextValue);
  };

  const onCreateNewBrandClick = () => {
    if (!hasReachedAdvertisersLimit) {
      setModalAddAdvertiserOpen(true);
      onClosePopover();
      return;
    }
    dispatch(setShowModalSelectSubscription(true));
    onClosePopover();
  };

  const onConfigureLimitsClick = () => {
    dispatch(setModalOpenState("isModalConfigureBrandLimits", true));
    onClosePopover();
  };

  const onChangePasswordClick = () => {
    setModalChangePasswordOpen(true);
    onClosePopover();
  };

  if (!ROLES_PLATFORMS.includes(role)) return null;

  if (isLoading) return <Loader hasMarginTop={false} size={20} />;

  if (!shouldShowCreateBrandOption && options?.length < 1) return null;

  return (
    <>
      <Controller
        control={control}
        name="advertiserID"
        render={({ field }) => {
          const selectedOption = getOptionFromId(field.value);
          const emptySelection = !selectedOption;

          return (
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                ...sx,
              }}
            >
              <AdvertiserSelectorTriggerButton
                disabled={disabled}
                emptySelection={emptySelection}
                triggerPlaceholder={triggerPlaceholder}
                selectedOption={selectedOption}
                isPopoverOpen={isPopoverOpen}
                onToggle={onTogglePopover}
              />

              <Popover
                open={isPopoverOpen}
                anchorEl={anchorEl}
                onClose={onClosePopover}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                disableScrollLock={false}
                elevation={0}
                PaperProps={{
                  sx: { ...ADVERTISER_SELECTOR_POPOVER_PAPER_SX },
                }}
              >
                <AdvertiserSelectorPopoverSearch
                  searchQuery={searchQuery}
                  searchPlaceholderLabel={translate(
                    ADVERTISER_SELECTOR_SEARCH_PLACEHOLDER
                  )}
                  onSearchChange={onSearchQueryChange}
                />

                <AdvertiserSelectorPopoverBrandsCountRow
                  brandsCountLabel={translate(
                    options.length === 1
                      ? ADVERTISER_SELECTOR_BRANDS_COUNT_SINGLE
                      : ADVERTISER_SELECTOR_BRANDS_COUNT,
                    {
                      count: options.length,
                    }
                  )}
                />

                <Box
                  sx={{
                    maxHeight: 280,
                    overflowY: "auto",
                    p: "8px",
                  }}
                >
                  <AdvertiserSelectorPopoverList
                    filteredAdvertisers={filteredAdvertisers}
                    field={field}
                    getOptionFromId={getOptionFromId}
                    agencyID={agencyID}
                    showDeleteBrandButton={showDeleteBrandButton}
                    translate={translate}
                    onSelectAdvertiser={onSelectAdvertiserFromList}
                    onConfirmDeleteBrand={onConfirmDeleteFromRow}
                    emptyTitleLabel={translate(ADVERTISER_SELECTOR_NO_RESULTS)}
                    emptyHintLabel={translate(
                      ADVERTISER_SELECTOR_TRY_ANOTHER_NAME
                    )}
                  />
                </Box>

                {shouldShowCreateBrandOption ||
                shouldShowConfigureLimitsOption ||
                shouldShowChangePasswordOption ||
                shouldShowLogoutOption ? (
                  <AdvertiserSelectorPopoverCreateFooter
                    createBrandLabel={translate(CREATE_BRAND_BUTTON)}
                    configureLimitsLabel={translate(
                      ADVERTISER_SELECTOR_CONFIGURE_LIMITS_BUTTON
                    )}
                    changePasswordLabel={translate(PASSWORD_TITLE)}
                    logoutLabel={translate(LOGOUT)}
                    onCreateBrand={onCreateNewBrandClick}
                    onConfigureLimits={onConfigureLimitsClick}
                    onChangePassword={onChangePasswordClick}
                    onLogout={onLogout}
                    shouldShowCreateBrandOption={shouldShowCreateBrandOption}
                    shouldShowConfigureLimitsOption={
                      shouldShowConfigureLimitsOption
                    }
                    shouldShowChangePasswordOption={
                      shouldShowChangePasswordOption
                    }
                    shouldShowLogoutOption={shouldShowLogoutOption}
                  />
                ) : null}
              </Popover>
            </Box>
          );
        }}
      />

      {modalAddAdvertiserOpen ? (
        <ModalAddAdvertiser
          modalOpen={modalAddAdvertiserOpen}
          agencyID={agencyID}
          onCloseModal={() => {
            setModalAddAdvertiserOpen(false);
          }}
          onCallbackSave={async () => {
            await dispatch(renewToken());
            await getInitialData();
          }}
        />
      ) : null}

      {modalChangePasswordOpen ? (
        <ModalChangePassword
          modalOpen={modalChangePasswordOpen}
          onCloseModal={() => {
            setModalChangePasswordOpen(false);
          }}
        />
      ) : null}
    </>
  );
};
